/**
 * TalentBackgroundChecks — /professional/background-checks
 * Talent user's own view of every background check run on them.
 * Two actions: request candidate copy + open a dispute.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Loader2, FileText, AlertCircle, ChevronDown, ChevronUp, CheckCircle2, Flag, Info, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const StatusChip = ({ label, status }) => {
    const s = (status || "").toLowerCase();
    const tone = s === "clear" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40"
        : ["consider", "consider_review_required"].includes(s) ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40"
        : s === "dispute" ? "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/40"
        : "bg-muted text-foreground border-border";
    return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${tone}`}>{label || s}</span>;
};

const RowActions = ({ row, onReload }) => {
    const [requestingCopy, setRequestingCopy] = useState(false);
    const [downloadingConsent, setDownloadingConsent] = useState(false);
    const [showDispute, setShowDispute] = useState(false);
    const [inaccuracy, setInaccuracy] = useState("");
    const [details, setDetails] = useState("");
    const [submittingDispute, setSubmittingDispute] = useState(false);

    const requestCopy = async () => {
        setRequestingCopy(true);
        try {
            await api.post(`/talent/background-checks/${row.id}/request-copy`);
            toast.success("We've asked Checkr to send you a copy.");
            onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not request a copy.");
        } finally {
            setRequestingCopy(false);
        }
    };

    const downloadConsentPdf = async () => {
        setDownloadingConsent(true);
        try {
            const res = await api.get(
                `/talent/background-checks/${row.id}/consent-pdf`,
                { responseType: "blob" },
            );
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `simonara-signed-consent-${row.id}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Signed consent PDF downloaded.");
        } catch (err) {
            // Axios blob responses stash JSON errors as a Blob, so decode.
            let msg = "Could not download the signed consent PDF.";
            const data = err?.response?.data;
            if (data && typeof data.text === "function") {
                try { msg = JSON.parse(await data.text())?.detail || msg; } catch { /* keep default */ }
            }
            toast.error(msg);
        } finally {
            setDownloadingConsent(false);
        }
    };

    const submitDispute = async (e) => {
        e.preventDefault();
        if (inaccuracy.trim().length < 3) {
            toast.error("Please describe what's inaccurate.");
            return;
        }
        setSubmittingDispute(true);
        try {
            await api.post(`/talent/background-checks/${row.id}/dispute`, {
                inaccuracy: inaccuracy.trim(), details: details.trim(),
            });
            toast.success("Dispute opened. We'll be in touch within 5 business days.");
            setShowDispute(false); setInaccuracy(""); setDetails("");
            onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not open dispute.");
        } finally {
            setSubmittingDispute(false);
        }
    };

    const copyState = row.candidate_copy_sent
        ? { label: `Copy sent ${row.candidate_copy_sent_at ? new Date(row.candidate_copy_sent_at).toLocaleDateString() : ""}`, disabled: true }
        : row.candidate_copy_requested_at
        ? { label: "Copy requested — pending", disabled: true }
        : { label: "Request my copy of the report", disabled: false };

    return (
        <div className="mt-4 space-y-3">
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={requestCopy}
                    disabled={copyState.disabled || requestingCopy}
                    data-testid={`tal-bg-request-copy-${row.id}`}
                    className="btn-outline text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
                >
                    {requestingCopy ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                    {copyState.label}
                </button>
                {row.source === "employer_requested" && row.consent_record?.consent_at && (
                    <button
                        type="button"
                        onClick={downloadConsentPdf}
                        disabled={downloadingConsent}
                        data-testid={`tal-bg-download-consent-${row.id}`}
                        className="btn-outline text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
                        title="Download the exact disclosure &amp; authorization you signed at consent time."
                    >
                        {downloadingConsent ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                        Download signed consent (PDF)
                    </button>
                )}
                {(!row.dispute || row.dispute.status === "resolved") && (
                    <button
                        type="button"
                        onClick={() => setShowDispute((v) => !v)}
                        data-testid={`tal-bg-dispute-toggle-${row.id}`}
                        className="btn-outline text-xs inline-flex items-center gap-1.5"
                    >
                        <Flag size={12} /> Dispute something in my report
                    </button>
                )}
                {row.dispute && row.dispute.status !== "resolved" && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-400" data-testid={`tal-bg-dispute-open-${row.id}`}>
                        <Flag size={11} /> Dispute open — {row.dispute.status}
                    </span>
                )}
            </div>

            {showDispute && (
                <form onSubmit={submitDispute} className="rounded-xl border border-border bg-muted/30 p-4 space-y-3" data-testid={`tal-bg-dispute-form-${row.id}`}>
                    <label className="block text-xs font-semibold text-foreground">
                        What is inaccurate?
                        <input value={inaccuracy} onChange={(e) => setInaccuracy(e.target.value)} maxLength={400} required data-testid={`tal-bg-dispute-inaccuracy-${row.id}`} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="e.g. Wrong middle name; wrong employer dates" />
                    </label>
                    <label className="block text-xs font-semibold text-foreground">
                        Details (optional)
                        <textarea value={details} onChange={(e) => setDetails(e.target.value)} maxLength={4000} rows={3} data-testid={`tal-bg-dispute-details-${row.id}`} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="Anything else that will help us investigate quickly." />
                    </label>
                    <div className="flex gap-2 justify-end">
                        <button type="button" onClick={() => setShowDispute(false)} className="btn-outline text-xs">Cancel</button>
                        <button type="submit" disabled={submittingDispute} className="btn-green text-xs inline-flex items-center gap-1.5" data-testid={`tal-bg-dispute-submit-${row.id}`}>
                            {submittingDispute ? <><Loader2 size={11} className="animate-spin" /> Sending…</> : "Open dispute"}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

const CheckRow = ({ row, onReload }) => {
    const [expanded, setExpanded] = useState(false);
    return (
        <div className="card-soft p-5" data-testid={`tal-bg-row-${row.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap">
                        <StatusChip label={row.result_label} status={row.status} />
                        <span className="text-sm font-semibold text-foreground">{row.employer_company}</span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                        {row.package_label} · Requested {row.requested_at ? new Date(row.requested_at).toLocaleDateString() : "—"}
                        {row.completed_at && ` · Completed ${new Date(row.completed_at).toLocaleDateString()}`}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    data-testid={`tal-bg-expand-${row.id}`}
                    aria-expanded={expanded}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
                >
                    {expanded ? "Hide details" : "Show details"} {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
            </div>

            {expanded && (
                <div className="mt-4 rounded-xl border border-border bg-muted/30 p-4 text-sm text-foreground space-y-2" data-testid={`tal-bg-consent-${row.id}`}>
                    <div className="font-semibold text-foreground text-sm">What you agreed to</div>
                    <div className="text-xs text-muted-foreground">
                        You reviewed and signed a background-check disclosure and authorization for <b className="text-foreground">{row.package_label}</b> through Checkr's secure hosted flow. That gave Checkr permission to gather the information described in the disclosure to run this check.
                    </div>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        <div><dt className="text-muted-foreground">Consent signed</dt><dd className="text-foreground">{row.consent_record?.consent_at ? new Date(row.consent_record.consent_at).toLocaleString() : "—"}</dd></div>
                        <div><dt className="text-muted-foreground">Method</dt><dd className="text-foreground">{row.consent_record?.consent_method || "Checkr-hosted flow"}</dd></div>
                        <div><dt className="text-muted-foreground">Disclosure version</dt><dd className="text-foreground"><code>{row.consent_record?.disclosure_version || "—"}</code></dd></div>
                        <div><dt className="text-muted-foreground">Authorization version</dt><dd className="text-foreground"><code>{row.consent_record?.authorization_version || "—"}</code></dd></div>
                    </dl>
                    <RowActions row={row} onReload={onReload} />
                </div>
            )}
        </div>
    );
};

const Explainer = () => (
    <section className="card-soft p-5 border-emerald-500/30 bg-emerald-500/5" data-testid="tal-bg-explainer">
        <div className="flex items-start gap-3">
            <Info size={16} className="text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
            <div className="text-sm text-foreground space-y-2 leading-relaxed">
                <div className="font-heading font-semibold text-base">About background checks on Simonara</div>
                <p><b>Who runs them:</b> Simonara orders and orchestrates every check; Checkr, our licensed consumer-reporting agency, produces the underlying consumer report.</p>
                <p><b>What's collected:</b> only the information the specific check needs — for a criminal check that's your identifiers (name, date of birth, SSN); for MVR it also includes your driver's license; for employment/education it's contact information for prior workplaces or schools. We always tell you before anything runs.</p>
                <p><b>Consent first, always:</b> nothing runs without your written consent through Checkr's secure flow. You can withdraw consent anytime.</p>
                <p><b>How long we keep the data:</b> <b className="text-foreground">2 years after completion</b> by default. Longer only if we're legally required to (e.g. an active dispute, adverse-action window, or subpoena).</p>
            </div>
        </div>
    </section>
);

export default function TalentBackgroundChecks() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/talent/background-checks");
            setRows(data.results || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load your background checks.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="container-app py-10" data-testid="talent-bg-checks-page">
            <header>
                <p className="overline inline-flex items-center gap-1"><ShieldCheck size={11} /> Your data</p>
                <h1 className="mt-2 font-heading font-semibold text-3xl tracking-tight text-foreground">Background checks</h1>
                <p className="mt-1 text-sm text-muted-foreground">Every check ever run on you — and everything you agreed to.</p>
            </header>

            <div className="mt-6"><Explainer /></div>

            <div className="mt-6 space-y-3">
                {loading ? (
                    <div className="py-14 flex justify-center"><Loader2 className="animate-spin text-emerald-500" /></div>
                ) : rows.length === 0 ? (
                    <div className="card-soft p-10 text-center" data-testid="tal-bg-empty">
                        <ShieldCheck size={24} className="mx-auto text-muted-foreground" />
                        <div className="mt-3 font-heading font-semibold text-lg text-foreground">No background checks yet.</div>
                        <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                            When an employer requests one, you'll be asked for consent first and it will appear here.
                        </p>
                        <Link to="/professional/verify" className="mt-4 btn-outline text-xs inline-flex items-center gap-1.5" data-testid="tal-bg-empty-cta">
                            Explore Simonara Verify <CheckCircle2 size={12} />
                        </Link>
                    </div>
                ) : (
                    rows.map((r) => <CheckRow key={r.id} row={r} onReload={load} />)
                )}
            </div>

            <div className="mt-8 text-xs text-muted-foreground" data-testid="tal-bg-privacy-note">
                Only you can see this list. We never share your report with anyone you haven&apos;t explicitly authorized.
                Questions? <Link to="/privacy" className="underline hover:text-foreground">Privacy policy</Link>.
            </div>
        </div>
    );
}
